/**
 * ProspectsPage.jsx — components/prospects/ProspectsPage.jsx
 *
 * Scalping candidate scanner. Three features:
 *   1. SPY trend warning banner
 *   2. Prospect cards sorted by oscillation score
 *   3. Prospects persist in component state — auto-load only populates
 *      if we have NO data yet (prevents overwriting fresh scans)
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import EarningsDashboard from '../earnings/EarningsDashboard';
import { getToken } from '../../lib/api';

const WORKER_BASE = import.meta.env.PROD
  ? 'https://options-dashboard-api.ladiida.workers.dev/api'
  : '/api';

function authHeaders() {
  return { 'Content-Type': 'application/json', 'x-session-token': getToken() };
}

// localStorage backup only — not primary source
const LS_KEY = 'od:prospects';
const MIN_FETCH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes between auto-fetches
let lastAutoFetchAt = 0; // module-level so it survives remounts
function loadLocalCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > 8 * 3600 * 1000) return null; // 8h
    return { data, ts };
  } catch { return null; }
}
function saveLocalCache(data) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

const fmt = (n, d = 1) => n == null ? '—' : Number(n).toFixed(d);

function ratingColor(r) {
  return r === 'excellent' ? 'var(--up)' : r === 'good' ? 'var(--accent)' : r === 'fair' ? 'var(--warn)' : 'var(--down)';
}

// ── SPY Banner ────────────────────────────────────────────────────────────────
function SpyBanner({ spy }) {
  if (!spy) return null;
  const condColor = { excellent: 'var(--up)', good: 'var(--accent)', fair: 'var(--warn)' }[spy.scalpConditions] || 'var(--down)';
  const trendColor = (spy.trend === 'ranging' || spy.trend === 'choppy') ? 'var(--up)' : 'var(--warn)';
  return (
    <div style={{
      background: spy.trendWarning ? 'var(--down-soft)' : 'var(--up-soft)',
      border: `1px solid ${spy.trendWarning ? 'var(--down)' : 'var(--up)'}`,
      borderRadius: 8, padding: '14px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 18 }}>{spy.trendWarning ? '⚠️' : '✅'}</span>
            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)' }}>
              SPY — {spy.trendWarning ? 'TREND WARNING' : 'Scalp-Friendly'}
            </span>
          </div>
          {spy.trendWarning && spy.trendWarningMsg && (
            <div style={{ fontSize: 12, color: 'var(--down)', marginBottom: 6, fontWeight: 600 }}>{spy.trendWarningMsg}</div>
          )}
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{spy.regimeNote}</div>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {[
            ['Trend',      spy.trend,                              trendColor],
            ['Trend str.', spy.trendStrength,                     spy.trendStrength > 60 ? 'var(--down)' : 'var(--up)'],
            ['VIX',        spy.vix != null ? fmt(spy.vix) : '—', spy.vix > 25 ? 'var(--down)' : 'var(--text)'],
            ['Conditions', spy.scalpConditions,                   condColor],
          ].map(([l, v, c]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 9, color: 'var(--faint)', letterSpacing: '.1em', textTransform: 'uppercase' }}>{l}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: c, marginTop: 2, textTransform: 'uppercase' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Oscillation bar ───────────────────────────────────────────────────────────
function OscBar({ score, trendStrength }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>OSCILLATION ↑</div>
        <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${score}%`, background: score >= 70 ? 'var(--up)' : score >= 45 ? 'var(--warn)' : 'var(--down)', borderRadius: 3 }} />
        </div>
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: score >= 70 ? 'var(--up)' : score >= 45 ? 'var(--warn)' : 'var(--down)', minWidth: 22 }}>{score}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 9, color: 'var(--faint)', marginBottom: 2 }}>TREND STR. ↓</div>
        <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${trendStrength}%`, background: trendStrength >= 60 ? 'var(--down)' : trendStrength >= 35 ? 'var(--warn)' : 'var(--up)', borderRadius: 3 }} />
        </div>
      </div>
      <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: trendStrength >= 60 ? 'var(--down)' : trendStrength >= 35 ? 'var(--warn)' : 'var(--up)', minWidth: 22 }}>{trendStrength}</span>
    </div>
  );
}

// ── Prospect Card ─────────────────────────────────────────────────────────────
function ProspectCard({ stock, rank, expanded, onToggle }) {
  const rc = ratingColor(stock.scalpRating);
  const mc = stock.momentum === 'oscillating' ? 'var(--up)' : stock.momentum === 'uptrend' ? 'var(--accent)' : stock.momentum === 'downtrend' ? 'var(--down)' : 'var(--warn)';
  const mi = stock.momentum === 'oscillating' ? '⟺' : stock.momentum === 'uptrend' ? '↑' : stock.momentum === 'downtrend' ? '↓' : '⚡';

  return (
    <div style={{ background: 'var(--bg-elev)', border: `1px solid ${rank === 0 ? 'var(--up)' : 'var(--border)'}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
      {rank === 0 && <div style={{ background: 'var(--up)', padding: '3px 12px' }}><span style={{ fontSize: 10, fontWeight: 700, color: 'var(--bg)', letterSpacing: '.1em' }}>⟺ TOP SCALP CANDIDATE</span></div>}
      <div onClick={onToggle} style={{ display: 'grid', gridTemplateColumns: '44px 1fr auto auto auto', gap: '0 10px', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: expanded ? 'var(--bg-hover)' : 'transparent' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: rank < 3 ? 'var(--up)' : 'var(--muted)' }}>#{rank + 1}</div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{stock.ticker}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: mc }}>{mi} {stock.momentum}</span>
            <span style={{ fontSize: 9, padding: '1px 6px', background: 'var(--border)', color: rc, borderRadius: 3, fontWeight: 700, textTransform: 'uppercase' }}>{stock.scalpRating}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{stock.company} · {stock.sector}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600 }}>${fmt(stock.currentPrice, 2)}</div>
          <div style={{ fontSize: 10, color: (stock.dayChange || 0) >= 0 ? 'var(--up)' : 'var(--down)', fontFamily: 'monospace' }}>{(stock.dayChange || 0) >= 0 ? '+' : ''}{fmt(stock.dayChange)}%</div>
        </div>
        <div style={{ textAlign: 'center', minWidth: 50 }}>
          <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: 'var(--warn)' }}>{fmt(stock.dayRangePct)}%</div>
          <div style={{ fontSize: 9, color: 'var(--faint)' }}>day rng</div>
        </div>
        <div style={{ color: 'var(--faint)', fontSize: 14, userSelect: 'none' }}>{expanded ? '▲' : '▼'}</div>
      </div>
      <div style={{ padding: '0 14px 8px' }}><OscBar score={stock.oscillationScore || 0} trendStrength={stock.trendStrength || 0} /></div>
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid var(--border)' }}>
            {[
              ['IV30',        `${fmt(stock.iv30)}%`,         stock.iv30 >= 50 ? 'var(--down)' : 'var(--text)'],
              ['IV Rank',     String(stock.ivRank ?? '—'),   stock.ivRank >= 70 ? 'var(--down)' : stock.ivRank >= 40 ? 'var(--warn)' : 'var(--text)'],
              ['Beta',        fmt(stock.beta),               (stock.beta || 0) >= 1.5 ? 'var(--up)' : 'var(--text)'],
              ['Vol vs Avg',  `${fmt(stock.volumeVsAvg)}×`, (stock.volumeVsAvg || 0) >= 1.5 ? 'var(--up)' : 'var(--text)'],
              ['Avg Rng/Day', `${fmt(stock.avgDayRangePct)}%`, 'var(--text)'],
              ['Reversals',   String(stock.reversalCount ?? '—'), 'var(--up)'],
              ['Opt. Spread', stock.optionSpread || '—',     stock.optionSpread === 'tight' ? 'var(--up)' : stock.optionSpread === 'wide' ? 'var(--down)' : 'var(--warn)'],
              ['Expiry',      stock.suggestedExpiry || '—',  'var(--accent)'],
            ].map(([lbl, val, color]) => (
              <div key={lbl} style={{ padding: '8px 12px', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 3 }}>{lbl}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            <div style={{ padding: '10px 14px', borderRight: '1px solid var(--border)' }}>
              <div style={{ fontSize: 9, color: 'var(--up)', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>⟺ Why scalp this</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{stock.scalpReason}</div>
            </div>
            <div style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 9, color: 'var(--warn)', letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>⚠ Risk note</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{stock.riskNote}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
// ── IV Leaders Tab ────────────────────────────────────────────────────────────
// Non-AI data: Yahoo Finance predefined screeners (high_implied_volatility,
// day_gainers, day_losers) merged and sorted by move × IV. Shows previous
// session's highest-IV stocks and strongest directional movers.

function IVLeadersTab({ workerBase, authHeaders }) {
  const [data,     setData]    = useState(null);
  const [loading,  setLoading] = useState(false);
  const [error,    setError]   = useState(null);
  const [filter,   setFilter]  = useState('all'); // all | bull | bear | iv
  const [ts,       setTs]      = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${workerBase}/screener/iv-leaders`, { headers: authHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d.leaders ?? []);
      setTs(d.fetchedAt);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [workerBase]);

  useEffect(() => { load(); }, [load]);

  const leaders = (data ?? []).filter(s =>
    filter === 'all'  ? true :
    filter === 'bull' ? s.direction === 'BULL' :
    filter === 'bear' ? s.direction === 'BEAR' :
    filter === 'iv'   ? s.iv != null : true
  );

  const dirColor = dir => dir === 'BULL' ? 'var(--up)' : dir === 'BEAR' ? 'var(--down)' : 'var(--faint)';
  const dirIcon  = dir => dir === 'BULL' ? '↑' : dir === 'BEAR' ? '↓' : '→';

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:16 }}>
        <div style={{ fontSize:18, fontWeight:800, color:'var(--text)', marginBottom:4 }}>IV Leaders</div>
        <div style={{ fontSize:11, color:'var(--muted)', lineHeight:1.6 }}>
          Previous session's highest implied volatility stocks and strongest directional movers.
          Data from Yahoo Finance screeners — no AI, no predictions. Refreshes every 15 minutes.
          High IV + strong direction = best debit spread candidates for the next session.
        </div>
      </div>

      {/* Controls */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', gap:4 }}>
          {[['all','All'],['bull','↑ Bullish'],['bear','↓ Bearish'],['iv','High IV Only']].map(([k,l])=>(
            <button key={k} onClick={()=>setFilter(k)} style={{
              padding:'5px 12px', borderRadius:4, border:'1px solid var(--border)', cursor:'pointer',
              fontSize:11, fontWeight:600,
              background:filter===k?'var(--accent)':'transparent',
              color:filter===k?'var(--bg)':'var(--muted)',
            }}>{l}</button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {ts && <span style={{ fontSize:9, color:'var(--faint)', fontFamily:'monospace' }}>
            Updated {new Date(ts).toLocaleTimeString()}
          </span>}
          <button onClick={load} disabled={loading} style={{
            padding:'5px 12px', borderRadius:4, border:'1px solid var(--border)', cursor:'pointer',
            fontSize:11, color:'var(--muted)', background:'transparent',
          }}>{loading ? '⟳ Loading…' : '↺ Refresh'}</button>
        </div>
      </div>

      {error && <div style={{ padding:'10px 14px', borderRadius:6, border:'1px solid var(--down)', color:'var(--down)', fontSize:11, marginBottom:12 }}>Error: {error}</div>}

      {/* Legend */}
      <div style={{ display:'flex', gap:16, marginBottom:10, fontSize:9, color:'var(--faint)', flexWrap:'wrap' }}>
        <span>Score = |% move| × 2 + IV × 0.3 (higher = better candidate)</span>
        <span>Vol× = today's volume vs 3-month average</span>
        <span>IV = implied volatility from options (if available)</span>
      </div>

      {/* Table */}
      {!loading && leaders.length === 0 && (
        <div style={{ textAlign:'center', padding:'30px', color:'var(--faint)', fontSize:12 }}>
          {data ? 'No stocks match this filter.' : 'Click Refresh to load data.'}
        </div>
      )}

      {leaders.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                {['#','Ticker','Name','Price','Change','Direction','IV%','Vol×','Mkt Cap','Score','Sources'].map((h,i)=>(
                  <th key={i} style={{ padding:'6px 10px', textAlign:i>3?'center':'left',
                    fontSize:9, textTransform:'uppercase', letterSpacing:'.08em', color:'var(--faint)', fontWeight:700,
                    whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leaders.map((s, i) => {
                const chg = Number(s.chgPct);
                const isHighIV = s.iv && Number(s.iv) >= 50;
                return (
                  <tr key={s.ticker} style={{ borderBottom:'1px solid var(--border)',
                    background: i%2===0?'transparent':'var(--bg-elev,transparent)' }}>
                    <td style={{ padding:'8px 10px', color:'var(--faint)', fontSize:10 }}>{i+1}</td>
                    <td style={{ padding:'8px 10px', fontWeight:800, fontFamily:'monospace', color:'var(--text)' }}>
                      {s.ticker}
                      {isHighIV && <span style={{ marginLeft:4, fontSize:8, padding:'1px 4px', borderRadius:2,
                        background:'#f97316', color:'white', fontWeight:700 }}>HIGH IV</span>}
                    </td>
                    <td style={{ padding:'8px 10px', color:'var(--muted)', maxWidth:160,
                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</td>
                    <td style={{ padding:'8px 10px', fontFamily:'monospace' }}>${s.price}</td>
                    <td style={{ padding:'8px 10px', fontFamily:'monospace', fontWeight:700, textAlign:'center',
                      color:chg>0?'var(--up)':chg<0?'var(--down)':'var(--faint)' }}>
                      {chg>0?'+':''}{s.chgPct}%
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center' }}>
                      <span style={{ fontSize:13, fontWeight:800, color:dirColor(s.direction) }}>
                        {dirIcon(s.direction)}
                      </span>
                      {Math.abs(chg) >= 3 && (
                        <span style={{ marginLeft:4, fontSize:8, padding:'1px 4px', borderRadius:2,
                          background:dirColor(s.direction)+'30', color:dirColor(s.direction), fontWeight:700 }}>
                          STRONG
                        </span>
                      )}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center',
                      color:isHighIV?'#f97316':'var(--muted)', fontFamily:'monospace', fontWeight:isHighIV?700:400 }}>
                      {s.iv ? `${s.iv}%` : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--muted)', fontFamily:'monospace' }}>
                      {s.volRatio ? `${s.volRatio}×` : '—'}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center', color:'var(--faint)', fontFamily:'monospace' }}>
                      {s.mktCap ?? '—'}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center', fontFamily:'monospace',
                      fontWeight:700, color:Number(s.score)>=20?'var(--up)':Number(s.score)>=10?'var(--warn)':'var(--faint)' }}>
                      {s.score}
                    </td>
                    <td style={{ padding:'8px 10px', textAlign:'center', fontSize:9, color:'var(--faint)' }}>
                      {s.sources.join(', ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* How to use */}
      <div style={{ marginTop:20, padding:'12px 14px', borderRadius:7, border:'1px solid var(--border)',
        background:'var(--bg)', fontSize:11, lineHeight:1.7, color:'var(--muted)' }}>
        <div style={{ fontWeight:700, color:'var(--text)', marginBottom:6 }}>How to use this for next session</div>
        <div>
          <span style={{ color:'var(--up)', fontWeight:700 }}>↑ Bullish + High IV:</span> Trend candidate — consider a <strong>call debit spread</strong> at tomorrow's open if the gap confirms direction.
        </div>
        <div>
          <span style={{ color:'var(--down)', fontWeight:700 }}>↓ Bearish + High IV:</span> Trend candidate — consider a <strong>put debit spread</strong> at open if direction holds.
        </div>
        <div>
          <span style={{ color:'var(--faint)' }}>→ Flat + High IV:</span> Range-bound candidate — consider a <strong>credit spread</strong> (iron condor) if no news catalyst.
        </div>
        <div style={{ marginTop:6, color:'var(--faint)', fontSize:10 }}>
          Note: IV data availability depends on Yahoo Finance screener. Missing IV means the stock wasn't in the high-IV screener result — it may still have high IV but wasn't in the top 25.
        </div>
      </div>
    </div>
  );
}

export default function ProspectsPage({ t }) {
  const [innerTab, setInnerTab] = useState('prospects');
  const [data,        setData]        = useState(null);
  const [cacheTs,     setTs]          = useState(null);
  const [sharedCache, setSharedCache] = useState(false);
  const [syncing,     setSyncing]     = useState(false);
  const [error,       setError]       = useState(null);
  const [expanded,    setExpanded]    = useState(0);
  const [filter,      setFilter]      = useState('all');

  // Track whether we have user-initiated fresh data — if so, ignore KV auto-load
  const hasFreshData = useRef(false);

  // Auto-load ONLY if we have no data yet — guarded by min interval to prevent
  // hammering the API on rapid mount/unmount cycles or React StrictMode double-invoke
  useEffect(() => {
    if (hasFreshData.current) return;
    const now = Date.now();
    if (now - lastAutoFetchAt < MIN_FETCH_INTERVAL_MS) {
      // Try localStorage fallback silently without hitting the network
      const local = loadLocalCache();
      if (local?.data) { setData(local.data); setTs(local.ts); setSharedCache(false); }
      return;
    }
    lastAutoFetchAt = now;
    setSyncing(true);
    fetch(`${WORKER_BASE}/prospects`, { headers: authHeaders() })
      .then(r => {
        if (r.status === 429) throw new Error('rate_limited');
        return r.ok ? r.json() : Promise.reject(r.status);
      })
      .then(d => {
        if (d.error) throw new Error(d.error);
        if (!hasFreshData.current) {
          setData(d);
          setTs(d.fetchedAt || Date.now());
          setSharedCache(d.cached ?? false);
        }
      })
      .catch(() => {
        // Fall back to localStorage if Worker fails or rate-limited
        const local = loadLocalCache();
        if (local?.data && !hasFreshData.current) {
          setData(local.data);
          setTs(local.ts);
          setSharedCache(false);
        }
      })
      .finally(() => setSyncing(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = useCallback(async () => {
    setSyncing(true); setError(null);
    try {
      const r = await fetch(`${WORKER_BASE}/prospects`, { headers: authHeaders() });
      const ct = r.headers.get('content-type') || '';
      if (r.status === 429) throw new Error('Rate limited (429) — please wait a few minutes before scanning again.');
      if (!r.ok || !ct.includes('json')) throw new Error(`API ${r.status}: ${await r.text().then(t => t.slice(0, 120))}`);
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      hasFreshData.current = true; // mark so auto-load won't overwrite
      lastAutoFetchAt = Date.now(); // reset cooldown
      setData(d);
      saveLocalCache(d);
      setTs(d.fetchedAt || Date.now());
      setSharedCache(d.cached ?? false);
      setExpanded(0);
    } catch(e) { setError(e.message); }
    finally { setSyncing(false); }
  }, []);

  const age = cacheTs ? (() => {
    const m = Math.round((Date.now() - cacheTs) / 60000);
    return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
  })() : null;

  const prospects = (data?.prospects || []).filter(p =>
    filter === 'all' ? true :
    filter === 'excellent' ? p.scalpRating === 'excellent' :
    ['excellent','good'].includes(p.scalpRating)
  );

  const INNER_TABS = [
    { key: 'prospects', label: '⟺ Prospects' },
    { key: 'iv',        label: '🔥 IV Leaders' },
    { key: 'earnings',  label: '📅 Earnings'  },
    { key: 'vol',       label: '📊 Vol Analysis' },
  ];

  const tabBar = (
    <div style={{ display: 'flex', gap: 2, background: 'var(--bg)', borderRadius: 6, padding: 3, marginBottom: 20, width: 'fit-content' }}>
      {INNER_TABS.map(({ key, label }) => (
        <button key={key} onClick={() => setInnerTab(key)} style={{
          padding: '6px 16px', borderRadius: 4, border: 'none', cursor: 'pointer',
          fontSize: 12, fontWeight: 600, letterSpacing: '.04em',
          background: innerTab === key ? 'var(--accent)' : 'transparent',
          color: innerTab === key ? 'var(--bg)' : 'var(--muted)',
          transition: 'all .15s',
        }}>{label}</button>
      ))}
    </div>
  );

  if (innerTab === 'earnings' || innerTab === 'vol') {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 20px 60px' }}>
        {tabBar}
        <EarningsDashboard t={t} defaultTab={innerTab === 'vol' ? 'vol' : 'earnings'} hideTabBar={true} />
      </div>
    );
  }

  if (innerTab === 'iv') {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 20px 60px' }}>
        {tabBar}
        <IVLeadersTab workerBase={WORKER_BASE} authHeaders={authHeaders} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 16px 60px' }}>
      {tabBar}
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <button onClick={sync} disabled={syncing} style={{
          background: syncing ? 'var(--bg-hover)' : 'var(--accent-soft)', color: 'var(--accent)',
          border: '1px solid var(--accent-soft)', padding: '6px 14px', borderRadius: 5,
          fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase',
          cursor: syncing ? 'not-allowed' : 'pointer', opacity: syncing ? .6 : 1,
        }}>
          {syncing ? '⟳ Scanning…' : '⟳ Scan Prospects'}
        </button>
        <span style={{ fontSize: 11, color: 'var(--faint)', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6 }}>
          {age ? (
            <>
              <span style={{ width: 7, height: 7, borderRadius: '50%', display: 'inline-block', background: 'var(--up)', boxShadow: '0 0 6px var(--up)' }} />
              {age}
              {data?.dataDate && <span style={{ color: 'var(--muted)' }}>· {data.dataDate}</span>}
              {sharedCache && <span style={{ fontSize: 9, background: 'var(--accent-soft)', color: 'var(--accent)', padding: '1px 6px', borderRadius: 3, fontWeight: 700 }}>☁ SHARED</span>}
            </>
          ) : syncing ? null : 'No data — click Scan'}
        </span>
        {data && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {[['all','All'],['good','Good+'],['excellent','Excellent']].map(([k,l]) => (
              <button key={k} onClick={() => setFilter(k)} style={{
                background: filter === k ? 'var(--accent)' : 'var(--bg-hover)', color: filter === k ? 'var(--bg)' : 'var(--muted)',
                border: '1px solid var(--border)', padding: '3px 9px', borderRadius: 4, fontSize: 10, fontWeight: filter === k ? 700 : 400, cursor: 'pointer',
              }}>{l}</button>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: 'var(--down-soft)', border: '1px solid var(--down)', borderRadius: 6, padding: '10px 14px', color: 'var(--down)', fontSize: 12, marginBottom: 12 }}>
          ⚠ {error}
          <button onClick={sync} style={{ marginLeft: 10, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Retry</button>
        </div>
      )}

      {data?.spyAnalysis && <SpyBanner spy={data.spyAnalysis} />}
      {data?.sessionNote && <div style={{ padding: '8px 14px', marginBottom: 14, fontSize: 12, color: 'var(--muted)', borderLeft: '3px solid var(--accent)', lineHeight: 1.5 }}>{data.sessionNote}</div>}

      {!data && !syncing && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--faint)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⟺</div>
          <div style={{ fontSize: 14, marginBottom: 6, color: 'var(--text)' }}>Scalp Prospects</div>
          <div style={{ fontSize: 12, maxWidth: 380, margin: '0 auto', lineHeight: 1.7 }}>
            Identifies best stocks for 2-hour intraday options scalping.<br/>
            Prioritises <strong style={{ color: 'var(--up)' }}>oscillation over trend</strong>.
          </div>
          <button onClick={sync} style={{ marginTop: 20, background: 'var(--accent-soft)', color: 'var(--accent)', border: '1px solid var(--accent-soft)', padding: '10px 24px', borderRadius: 5, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ⟳ Scan Prospects
          </button>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--faint)' }}>~$0.001 per scan · Claude Haiku</div>
        </div>
      )}

      {syncing && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--muted)', fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 12 }}>
            {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--up)', animation: `pulse 1.2s ease-in-out ${i*.2}s infinite` }} />)}
          </div>
          <style>{`@keyframes pulse{0%,80%,100%{opacity:.2;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}`}</style>
          Scanning for scalp candidates…
        </div>
      )}

      {prospects.map((s, i) => (
        <ProspectCard key={s.ticker} stock={s} rank={i} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? -1 : i)} />
      ))}

      {data && prospects.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: 'var(--faint)', fontSize: 12 }}>No prospects match this filter.</div>}
      {data && <div style={{ marginTop: 20, fontSize: 10, color: 'var(--faint)', textAlign: 'center', letterSpacing: '.04em' }}>AI-GENERATED ANALYSIS · NOT INVESTMENT ADVICE · VERIFY BEFORE TRADING</div>}
    </div>
  );
}
